import * as React from 'react';
import { useEffect, useState } from 'react';

import { Button, ExpandableSection, Flashbar, Header, SpaceBetween, Spinner } from '@amzn/awsui-components-react-v3';
import { RequestContextComments } from 'src/components/datausageagreement/requestContextComments';
import {
  D3AValidation,
  D3AValidationList,
  D3AValidationResult,
  GetRequestRequest,
  GetRequestResult,
  RoleMapping,
  UpdateRequestContextRequest,
  UpdateRequestContextResponse,
  UserRequestContext,
} from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { UsageAgreementForm } from 'src/components/datausageagreement/usageAgreementForm';
import { ValidateFormDataWithD3a } from 'src/components/datausageagreement/validateFormDataWithD3a';
import { createRequestContext, getRequest, updateRequestContext } from 'src/api/permissions';
import { activeGroupOrWorkspaceId } from 'src/components/dataadvisory/constants';
import {
  DataAgreement,
  ValidateContextForDataAgreementOutput,
  Validation,
  ValidationResult,
} from 'aws-sdk/clients/d3aservice';
import { getDataAgreement, validateContextForDataAgreement } from 'src/api/d3a';
import { RequestContextMetadataContainer } from 'src/components/datausageagreement/requestContextMetadataContainer';
import { OmniDUAContext } from 'src/components/datausageagreement/widgets/OmniDUAContext';
import { ManageResourceRoles } from 'src/components/common/resourceAuth/manageResourceRoles';
import { scrollUp } from 'src/components/utils/navigation';
import { FORM_CONTEXTS_FOR_REQUESTS } from 'src/components/datausageagreement/utils';

export interface RequestContextDetailProps {
  setContentType: any;
  setNotifications: React.Dispatch<React.SetStateAction<any[]>>;
  match: any;
  activeGroup: string;
  activeWorkspace?: any;
  userInfo?: any;
  requestContext?: UserRequestContext;
  setRequestContext: any;
  dataAgreementId?: string;
  workspaceNameMap?: Map<string, string>;
  displayMetadataContainer: boolean;
  displaySaveButton: boolean;
  omniContext?: OmniDUAContext;
  displayPermissionRoles?: boolean;
  requestContextOwnerOverride?: string;
  hasOptimisticApprovalEligibility?: boolean;
}

export const RequestContextDetail = React.forwardRef((props: RequestContextDetailProps, ref: React.Ref<any>) => {
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  // form state
  const [dataAgreement, setDataAgreement] = useState<DataAgreement>(undefined);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState([]);

  // validation state
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isUnknown, setIsUnknown] = useState<boolean>(true);
  const [validationList, setValidationList] = useState<Validation[]>([]);
  const [validationErrorMessage, setValidationErrorMessage] = useState<string>(undefined);
  const [validationLoading, setValidationLoading] = useState<boolean>(false);
  const [overrideMessage, setOverrideMessage] = useState<string>('');

  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  const [readOnly, setReadOnly] = useState<boolean>(true);

  if (!!ref) {
    React.useImperativeHandle(ref, () => ({
      save: save,
      isValidating: () => validationLoading || saveLoading,
    }));
  }

  useEffect(() => {
    props?.setContentType('form');
    handleRefresh();
  }, []);

  useEffect(() => {
    if (props.requestContext?.dataString) {
      setFormData(JSON.parse(props.requestContext.dataString));
    }
    if (!!props.requestContext?.metadata?.d3AValidationContext) {
      setOverrideMessage(props.requestContext.metadata.d3AValidationContext.overrideMessage);
      setIsValid(props.requestContext.metadata.d3AValidationContext.isValid);
      setIsUnknown(false);
      setValidationList(
        convertD3aValidationsToValidations(props.requestContext.metadata.d3AValidationContext.validationList),
      );
    }
  }, [props.requestContext]);

  async function fetchAndSetDataAgreement(): Promise<DataAgreement> {
    // return if dataAgreement is already present
    if (!!dataAgreement) return;

    // first, use the request context directly if it is provided
    let dataAgreementId;
    if (!!props.requestContext) {
      dataAgreementId = props.requestContext.requestSchema.dataAgreementId;
      setFormData(JSON.parse(props.requestContext.dataString));
    }
    // otherwise, data agreement ID must be provided.
    else {
      dataAgreementId = props.dataAgreementId;
    }
    let dataAgreementRes: DataAgreement = undefined;
    if (!!dataAgreementId) {
      const getDataAgreementOutput = await getDataAgreement({ id: dataAgreementId });
      dataAgreementRes =
        getDataAgreementOutput.dataAgreements.length > 0 ? getDataAgreementOutput.dataAgreements[0] : undefined;
      setDataAgreement(dataAgreementRes);
    }
    return dataAgreementRes;
  }

  const handleRefresh = async () => {
    setLoading(true);

    // If all the requests for this request context are approved/denied, we can set the form to readonly.
    let allRequestsApprovedOrDenied: boolean = false;
    try {
      if (props.requestContext?.accessRequestIds?.length > 0) {
        allRequestsApprovedOrDenied = true;
        const requestIds: string[] = props.requestContext.accessRequestIds;
        for (const requestId of requestIds) {
          const getRequestRequest: GetRequestRequest = {
            requestId: requestId,
          };
          const getRequestResponse: GetRequestResult = await getRequest(getRequestRequest);
          if (!['APPROVED', 'DENIED'].includes(getRequestResponse.requestItem.status)) {
            allRequestsApprovedOrDenied = false;
            break;
          }
        }
      }
    } catch (e) {
      console.error('Failed to get the request statuses for the request context: ', e);
    }

    if (
      !allRequestsApprovedOrDenied &&
      (!props.requestContext || activeGroupOrWorkspaceId(props) == props.requestContext.ownerId)
    ) {
      setReadOnly(false);
    }
    let dataAgreementRes = await fetchAndSetDataAgreement();
    setLoading(false);
    // set error message if data is not found, only for req context page.
    if (!dataAgreementRes || (props.displayMetadataContainer && !props.requestContext)) {
      const errorMsg = `Request context does not exist or you are not authorized to view the context!`;
      setFlashbarNotification('error', errorMsg);
    } else {
      setNotifications([]);
    }
  };

  const convertValidationsToD3aValidations = (validations: Validation[]) => {
    let convertedValidationList: D3AValidation[] = [];
    validations?.forEach((val) => {
      convertedValidationList.push({
        attribute: val.attribute,
        value: val.value,
        jsonPath: val.jsonPath,
        validationResult: val.validationResult as D3AValidationResult,
        validationTypes: val.d3aValidationTypes,
      });
    });
    return convertedValidationList;
  };

  const convertD3aValidationsToValidations = (validations: D3AValidation[]) => {
    let convertedValidationList: Validation[] = [];
    validations?.forEach((val) => {
      convertedValidationList.push({
        attribute: val.attribute,
        value: val.value,
        jsonPath: val.jsonPath,
        validationResult: val.validationResult as ValidationResult,
        d3aValidationTypes: val.validationTypes,
      });
    });
    return convertedValidationList;
  };

  const invokeAPIWithFlashbarNotifications = async (
    callbackFn: Function,
    successMessage: string = '',
  ): Promise<string> => {
    try {
      await callbackFn();
      if (successMessage) {
        setFlashbarNotification('success', successMessage, 'global');
        scrollUp();
      }
      return null;
    } catch (e) {
      console.error('Failed to invoke req context API', e);
      const errorMsg = `Failed to persist request context with cause: ${e.message}. Please contact Omni on-call!`;
      setFlashbarNotification('error', errorMsg);
      return errorMsg;
    }
  };

  const updateRequestContextWithAuthZRoleMapping = async (roleMapping: { [key: string]: RoleMapping }) => {
    if (!props.requestContext || !props.requestContext.requestContextId) {
      throw new Error('Request context is not present to update authZRoleMapping!');
    }
    const updateRequestContextRequest: UpdateRequestContextRequest = {
      id: props.requestContext.requestContextId,
      contextScope: 'AccessRequest',
      authZRoleMapping: roleMapping,
    };
    const updateRequestContextResponse: UpdateRequestContextResponse = await updateRequestContext(
      updateRequestContextRequest,
    );
    props.setRequestContext(updateRequestContextResponse.userRequestContext);
  };

  const save = async (): Promise<{ hasErrors: boolean; cause: string }> => {
    setSaveLoading(true);

    let convertedValidationList: D3AValidationList = convertValidationsToD3aValidations(validationList);
    let currentIsValid = isValid;
    // validate again before save
    if (!currentIsValid) {
      const { isValid: updateIsValid, validationList: updatedValList } = await validate();
      convertedValidationList = convertValidationsToD3aValidations(updatedValList);
      currentIsValid = updateIsValid;
    }

    //If any non D3A validations are present, ie. Json schema required, minLength, etc. display error and do not proceed ahead.
    if (!currentIsValid && convertedValidationList?.length > 0) {
      let nonD3aValidationFailures: string[] = convertedValidationList
        .filter((val) => !val['validationTypes'])
        .flatMap((val) => Object.values(val.validationResult))
        .map((val) => val?.reason);
      if (nonD3aValidationFailures.length > 0) {
        // some required json validations have failed! do not proceed
        console.log(nonD3aValidationFailures);
        setSaveLoading(false);
        const errorMsg = `Please fill all the required fields and try again! cause: {"${nonD3aValidationFailures.join(
          ', ',
        )}"}`;
        console.error(errorMsg);
        setFlashbarNotification('error', errorMsg);
        return { hasErrors: true, cause: errorMsg };
      } else if (!currentIsValid && !overrideMessage) {
        setSaveLoading(false);
        const errorMsg = `Please provide an Override message for the validation failures to proceed!`;
        console.error(errorMsg);
        setFlashbarNotification('error', errorMsg);
        return { hasErrors: true, cause: errorMsg };
      }
    }

    // if not already exists: create
    let cause: string = undefined;
    if (!props.requestContext) {
      cause = await invokeAPIWithFlashbarNotifications(async () => {
        const createRequestContextOutput = await createRequestContext({
          dataString: JSON.stringify(formData),
          metadata: {
            correspondenceMessages: [],
            d3AValidationContext: {
              isValid: currentIsValid,
              overrideMessage: overrideMessage,
              validationList: convertedValidationList,
            },
          },
          requestSchema: {
            dataAgreementId: dataAgreement.id,
            dataAgreementSchemaString: JSON.stringify(dataAgreement.jsonSchema),
            dataAgreementVersion: dataAgreement.versionId,
            dataAgreementName: dataAgreement.name,
            dataAgreementType: dataAgreement.type,
          },
          type: 'D3ADataUsageAgreement',
          contextScope: 'AccessRequest',
          ownerId: props.requestContextOwnerOverride ?? activeGroupOrWorkspaceId(props),
        });
        props.setRequestContext(createRequestContextOutput.userRequestContext);
      });
    } else {
      cause = await invokeAPIWithFlashbarNotifications(async () => {
        let updateRequestContextRequest: UpdateRequestContextRequest = {
          id: props.requestContext.requestContextId,
          contextScope: props.requestContext.contextScope,
          metadata: {
            d3AValidationContext: {
              isValid: currentIsValid,
              overrideMessage: overrideMessage,
              validationList: convertedValidationList,
            },
            correspondenceMessages: [...props.requestContext.metadata.correspondenceMessages],
          },
        };
        // only provide dataString if it has been edited - this will cause a new versionId to be created for the request context.
        if (
          props.requestContext?.dataString != undefined &&
          JSON.stringify(formData) !== props.requestContext?.dataString
        ) {
          updateRequestContextRequest.dataString = JSON.stringify(formData);
        }
        const updateRequestContextResponse: UpdateRequestContextResponse = await updateRequestContext(
          updateRequestContextRequest,
        );
        props.setRequestContext(updateRequestContextResponse.userRequestContext);
      }, 'Request context updated successfully. The underlying requests were updated with new AI recommendations.');
    }
    setSaveLoading(false);
    return { hasErrors: !!cause, cause };
  };

  const validate = async (): Promise<{ isValid: boolean; validationList: D3AValidationList }> => {
    setValidationLoading(true);
    let currentValidationList: D3AValidationList = [];
    let isValid: boolean = false;
    try {
      const validateContextForDataAgreementOutput: ValidateContextForDataAgreementOutput =
        await validateContextForDataAgreement({
          id: dataAgreement.id,
          versionId: dataAgreement.versionId,
          context: formData,
          validationContexts: FORM_CONTEXTS_FOR_REQUESTS,
        });
      setIsValid(validateContextForDataAgreementOutput.isValid);
      setValidationList(validateContextForDataAgreementOutput.validationList);
      setValidationErrorMessage(undefined);
      currentValidationList = validateContextForDataAgreementOutput.validationList;
      isValid = validateContextForDataAgreementOutput.isValid;
    } catch (e) {
      console.log('Error when validating form inputs: ', e);
      setIsValid(false);
      setValidationList([]);
      setValidationErrorMessage(`Internal error when validating: ${e.message}`);
    }
    setValidationLoading(false);
    setIsUnknown(false);
    return { isValid, validationList: currentValidationList };
  };

  const setFlashbarNotification = async (header, message, scope = 'validations') => {
    let setNotificationsFunc = setNotifications;
    if (scope === 'global') {
      setNotificationsFunc = props.setNotifications;
    }
    if (header === '' || header === 'success') {
      setNotificationsFunc([
        {
          type: 'success',
          content: message,
          dismissible: true,
          onDismiss: () => setNotificationsFunc([]),
        },
      ]);
    } else {
      setNotificationsFunc([
        {
          header: header,
          type: 'error',
          content: message,
          dismissible: true,
          onDismiss: () => setNotificationsFunc([]),
        },
      ]);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <SpaceBetween size={'m'} direction={'vertical'}>
      {props.displayMetadataContainer && props.requestContext && (
        <RequestContextMetadataContainer
          requestContext={props.requestContext}
          workspaceNameMap={props.workspaceNameMap}
        />
      )}
      {props.displayPermissionRoles && props.requestContext && (
        <ExpandableSection variant='container' headerText='Permission roles'>
          <ManageResourceRoles
            resourceId={props.requestContext?.requestContextId}
            resourceType={'requestContext'}
            headerText={''}
            authZRoleMapping={props.requestContext?.authZRoleMapping}
            onAuthZRoleMappingUpdate={updateRequestContextWithAuthZRoleMapping}
          />
        </ExpandableSection>
      )}
      {dataAgreement && (
        <UsageAgreementForm
          schema={JSON.stringify(dataAgreement.jsonSchema)}
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          setErrors={setErrors}
          readOnly={readOnly}
          omniContext={props.omniContext}
          contexts={FORM_CONTEXTS_FOR_REQUESTS}
        />
      )}
      {/* Display errors above validations, we cannot proceed if these are not fixed! */}
      {notifications?.length > 0 && <Flashbar items={notifications} />}
      {dataAgreement && (
        <ValidateFormDataWithD3a
          readonly={readOnly}
          loading={validationLoading}
          formData={formData}
          isValid={isValid}
          isUnknown={isUnknown}
          validationList={validationList}
          validationErrorMessage={validationErrorMessage}
          validate={validate}
          overrideReason={overrideMessage}
          setOverrideReason={setOverrideMessage}
          hasOptimisticApprovalEligibility={props.hasOptimisticApprovalEligibility}
        />
      )}
      {props.displaySaveButton && dataAgreement && (
        <Header
          actions={
            <Button disabled={readOnly || saveLoading} loading={saveLoading} variant={'normal'} onClick={save}>
              Save responses
            </Button>
          }
        />
      )}
      {props.requestContext && (
        <RequestContextComments
          readOnly={false}
          requestContext={props.requestContext}
          setRequestContext={props.setRequestContext}
        />
      )}
    </SpaceBetween>
  );
});
